import React, { useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import wordcloud from "highcharts/modules/wordcloud";
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import fullscreen from "highcharts/modules/full-screen";

// Initialize Highcharts modules
wordcloud(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

const ReportWordCloud = ({ wordcloudData, dataType = "hashtags" }) => {
  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) chart.reflow();
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!wordcloudData || typeof wordcloudData !== "object") {
    return null;
  }

  // Convert data to array and sort by weight
  const sortedData = Object.entries(wordcloudData)
    .map(([key, value]) => ({
      name: key,
      weight: value,
    }))
    .sort((a, b) => b.weight - a.weight);

  // Calculate optimal number of words to display (increased limit)
  const calculateOptimalCount = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportArea = viewportWidth * viewportHeight;

    const baseCount = Math.floor(viewportArea / 10000); // Reduced divisor to allow more words
    return Math.min(Math.max(20, baseCount), 200); // Increased max to 200 words
  };

  const optimalCount = calculateOptimalCount();

  // Color scheme
  const colorScheme = [
    "#0386e5",
    "#6610f2",
    "#6f42c1",
    "#e83e8c",
    "#dc3545",
    "#fd7e14",
    "#f8c346",
    "#28a745",
    "#20c997",
    "#17a2b8",
    "#6c757d",
    "#343a40",
    "#ff5733",
    "#c70039",
    "#900c3f",
    "#581845",
    "#1f77b4",
    "#ff6347",
    "#adff2f",
    "#ff1493",
    "#00fa9a",
    "#8a2be2",
    "#a52a2a",
    "#f4a300",
    "#008080",
    "#483d8b",
    "#e9967a",
  ];

  // Transform data - NO FILTERING by weight, only by count
  const transformedData = sortedData
    .slice(0, optimalCount) // Only limit by count, not by weight threshold
    .map((item, index) => {
      const maxWeight = sortedData[0].weight;
      const minWeight = sortedData[sortedData.length - 1].weight;

      // Ensure minimum font size for low-weight items
      const normalizedWeight =
        maxWeight > minWeight
          ? (item.weight - minWeight) / (maxWeight - minWeight)
          : 1;

      return {
        ...item,
        color: colorScheme[index % colorScheme.length],
        fontSize: Math.max(8, normalizedWeight * 35 + 10), // Minimum 8px font size
      };
    });
  const getTitleText = () => {
    const count = transformedData.length;
    if (dataType === "keywords") {
      return ` Keywords (${count} )`;
    } else {
      return ` Hashtags (${count} )`;
    }
  };
  const options = {
    chart: {
      type: "wordcloud",
      events: {
        load: function () {
          this.reflow();
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: getTitleText(),
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color}">●</span> <h1>{point.weight}</h1><br/>',
      style: {
        fontSize: "20px",
      },
    },
    plotOptions: {
      wordcloud: {
        minFontSize: 40, // Reduced minimum font size
        maxFontSize: 60,
        rotation: {
          from: -10,
          to: 10,
          orientations: 2,
        },
        placementStrategy: "center",
        spiral: "rectangular",
      },
    },
    series: [
      {
        type: "wordcloud",
        data: transformedData,
        rotation: {
          from: 0,
          to: 0,
        },
        colorByPoint: true,
      },
    ],
    exporting: {
      enabled: false,
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            title: {
              style: {
                fontSize: "16px",
              },
            },
            series: [
              {
                dataLabels: {
                  style: {
                    fontSize: "8px", // Reduced for mobile
                  },
                },
              },
            ],
          },
        },
      ],
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default ReportWordCloud;
